/* eslint-disable no-console */
// Helper functions for Optimizely
import { createInstance, enums } from '@optimizely/react-sdk';

import { OPTIMIZELY_URL } from 'consts/appConsts';
import { OPTIMIZELY_NOT_INITIALIZED } from 'consts/storageConsts';

import { LOG_LEVELS, loggerHelper } from 'utils/logger/loggerHelper';
import { getAnonymousId } from 'utils/segment/index';
import {
  trackFeatureEnabled,
  trackOptimizelyActivated,
} from 'utils/segment/trackingEvents';
import {
  getValueFromStorage,
  setValueInStorage,
} from 'utils/storage/storageHelper';

const options = { uri: OPTIMIZELY_URL, json: true };

export const OPTIMIZELY_SDK_STUB = {
  setUser: () => {},
  isReady: () => false,
  onReady: () => Promise.reject(),
  track: () => {},
  user: {},
  setForcedVariation: () => false,
};

// If we can't store in localStorage, we can still store temporarily in local session
// This can still be wiped out if user reloads the page
let optimizelyDatafile;

/**
 * Initialize Optimizely
 *
 * How it works: We first fetch a json datafile that contains a mapped list of experiments
 * We then initialize the optimizely client (optimizelySDK) by feeding it the fetched datafile
 *
 * Later we can then ask the optimizely client for the user's experiment variation without extra network calls
 */
export async function initOptimizely() {
  try {
    const datafile =
      getCachedDatafile() ||
      (await fetch(OPTIMIZELY_URL, options).then(res => res.json()));

    cacheDatafile(datafile);
    const optimizelyClient = createInstance({
      datafile,
      eventBatchSize: 10,
      eventFlushInterval: 1000,
    });

    optimizelyClient.setUser(getOptimizelyUserAttributes());

    await optimizelyClient.onReady();

    optimizelyClient.notificationCenter?.addNotificationListener(
      enums.NOTIFICATION_TYPES.DECISION,
      onOptimizelyDecision
    );

    optimizelyClient.notificationCenter?.addNotificationListener(
      enums.NOTIFICATION_TYPES.ACTIVATE,
      onOptimizelyActivated
    );

    return optimizelyClient;
  } catch (err) {
    setValueInStorage(OPTIMIZELY_NOT_INITIALIZED, true);
    window.optimizelyNotInitalized = true;

    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: 'Could not initialize optimizely',
      info: err,
    });

    return OPTIMIZELY_SDK_STUB;
  }
}

/**
 * Cache datafile so we don't have to fetch again everytime user reloads the page
 */
export function cacheDatafile(file) {
  try {
    optimizelyDatafile = file;
    localStorage.setItem('datafile', JSON.stringify(file));
  } catch (err) {
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: 'Could not cache Optimizely datafile',
      info: err,
    });
  }
}

/**
 * Grab cached datafile from localStorage
 */
export function getCachedDatafile() {
  let datafile;

  try {
    datafile =
      optimizelyDatafile || JSON.parse(localStorage.getItem('datafile'));
  } catch (err) {
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message:
        'Could not read Optimizely datafile from storage. Please allow cookies and storage for this site in order for it to work properly',
      info: err,
    });
  }

  return datafile;
}

export function getOptimizelyUserAttributes() {
  return {
    id: getAnonymousId(),
  };
}

function onOptimizelyActivated(
  experiment,
  userId,
  attributes,
  variation,
  event
) {
  trackOptimizelyActivated({
    experiment,
    userId,
    attributes,
    variation,
    event,
  });
}

export function onOptimizelyDecision({ decisionInfo, type }) {
  if (type === 'feature' || type === 'feature-variable') {
    const { featureEnabled, featureKey } = decisionInfo;
    const bucketedFlagName = `${featureKey}_bucketed`;

    if (!getValueFromStorage(bucketedFlagName)) {
      setValueInStorage(bucketedFlagName, 'true');
      trackFeatureEnabled(featureKey, featureEnabled);
    }
  }
}
